import React, { useMemo } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';
import { NamedLink } from '../../../components';
import { buildCategorySlugMap, buildCategoryPath, buildCategoryPathFromObjects } from '../../../util/categoryHelpers';

import css from './CategoryNavBar.module.css';

const CategoryNavBar = ({ className, rootClassName }) => {
  const config = useConfiguration();

  const categories = config?.categoryConfiguration?.categories || [];

  // Only show if there are categories
  if (!categories || categories.length === 0) {
    return null;
  }

  // Build slug map once
  const slugMap = useMemo(() => buildCategorySlugMap(categories), [categories]);

  const classes = classNames(rootClassName || css.root, className);

  // Calculate dynamic dropdown width and positioning based on number of columns needed
  const getDropdownClasses = (subcategories, categoryIndex, totalCategories) => {
    if (!subcategories || subcategories.length === 0) return css.dropdown;
    
    const itemCount = subcategories.length;
    const columnsNeeded = Math.ceil(itemCount / 5); // 5 items per column
    
    // Determine position based on category position in navbar
    const isFirstCategory = categoryIndex === 0;
    const isLastCategory = categoryIndex === totalCategories - 1;
    const isMiddleLeft = categoryIndex < totalCategories / 2;
    const isMiddleRight = categoryIndex >= totalCategories / 2;
    
    let positionClass = '';
    if (isFirstCategory || (isMiddleLeft && columnsNeeded > 2)) {
      positionClass = css.dropdownAlignLeft;
    } else if (isLastCategory || (isMiddleRight && columnsNeeded > 2)) {
      positionClass = css.dropdownAlignRight;
    } else {
      positionClass = css.dropdownAlignCenter;
    }
    
    return classNames(css.dropdown, positionClass, {
      [css.dropdownSingleColumn]: columnsNeeded === 1,
      [css.dropdownTwoColumns]: columnsNeeded === 2,
      [css.dropdownThreeColumns]: columnsNeeded === 3,
      [css.dropdownFourColumns]: columnsNeeded >= 4,
    });
  };

  return (
    <div className={classes}>
      <div className={css.container}>
        <nav className={css.navigation}>
          <ul className={css.categoryList}>
            {categories.map((category, index) => {
              const hasSubcategories = category.subcategories && category.subcategories.length > 0;
              // Use the simpler helper that works directly with the category object
              const categoryPath = buildCategoryPathFromObjects(category);

              // Skip if category path couldn't be built (shouldn't happen, but safety check)
              if (!categoryPath || !categoryPath.category) {
                console.warn(`Could not build category path for category: ${category.id}`);
                return null;
              }

              return (
                <li key={category.id} className={css.categoryItem}>
                  <div className={css.categoryWrapper}>
                    <NamedLink
                      name="CategoryPage"
                      params={{ category: categoryPath.category }}
                      className={classNames(css.categoryLink, {
                        [css.categoryLinkWithDropdown]: hasSubcategories
                      })}
                    >
                      {category.name}
                      {hasSubcategories && (
                        <span className={css.dropdownArrow}>▼</span>
                      )}
                    </NamedLink>

                    {hasSubcategories && (
                      <div className={getDropdownClasses(category.subcategories, index, categories.length)}>
                        <ul className={css.dropdownList}>
                          {category.subcategories.map((subcategory) => {
                            // Use the simpler helper that works directly with the objects we have
                            const subcategoryPath = buildCategoryPathFromObjects(category, subcategory);

                            // Skip if subcategory path couldn't be built (shouldn't happen)
                            if (!subcategoryPath || !subcategoryPath.category || !subcategoryPath.subcategory) {
                              console.warn(`Could not build subcategory path for: ${category.id}/${subcategory.id}`);
                              return null;
                            }

                            return (
                              <li key={subcategory.id} className={css.dropdownItem}>
                                <NamedLink
                                  name="CategoryPageWithSubcategory"
                                  params={{
                                    category: subcategoryPath.category,
                                    subcategory: subcategoryPath.subcategory
                                  }}
                                  className={css.dropdownLink}
                                >
                                  {subcategory.name}
                                </NamedLink>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default CategoryNavBar;